/** The hunt detail route's search parameters, read off the URL. */
export interface HuntDetailSearch {
  /** Where Close returns to. */
  from: string
  /** The hunt's team. Absent means the page cannot fetch anything team-scoped. */
  teamId?: string
  /** The hunt is over: the page is a report, not a plan. */
  ended: boolean
  /** The hunt is running: read-only, with a live indication. */
  ongoing: boolean
}

/**
 * Reads the hunt detail route's search parameters.
 *
 * Takes the router's own parsed search record rather than a raw query string,
 * and that distinction is the whole reason this function exists. The router
 * encodes a search value by JSON-stringifying it whenever the value parses as
 * JSON, so `navigate({ search: { ended: '1' } })` puts `ended=%221%22` — the
 * string `"1"` *with quotes* — in the address bar, and parses it back to `'1'`
 * on the way in. Reading the same URL with `URLSearchParams` instead skips that
 * decode and yields `"1"` quotes and all, so an `=== '1'` test fails on every
 * link the app itself produced: an ended hunt then rendered as a plan, and the
 * downed-game fetch — gated on the same flag — never ran, so a hunt with game
 * reported showed none and offered no error to explain it. Team ids and paths
 * survived that round trip only because their values are not valid JSON.
 *
 * A flag is on for `'1'` (what the router hands back for a link the app wrote)
 * and for `1` (what it hands back for a hand-typed `?ended=1`, which parses as
 * a number). Any other value is off: the app writes exactly one dialect, and a
 * page that guessed at another would fetch one thing while showing another.
 */
export function huntDetailSearch(search: Record<string, unknown>): HuntDetailSearch {
  return {
    from: typeof search.from === 'string' ? search.from : '/hunts',
    teamId: typeof search.teamId === 'string' ? search.teamId : undefined,
    ended: isFlagOn(search.ended),
    ongoing: isFlagOn(search.ongoing),
  }
}

function isFlagOn(raw: unknown): boolean {
  return raw === '1' || raw === 1
}
